import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '@/components/common/TextInput';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';
import { useLogin } from '@/features/auth/hooks/useAuth';
import { LoginCredentials } from '@/features/auth/types';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [direction, setDirection] = useState<'forward' | 'backward' | 'none'>('none');
  const navigate = useNavigate();
  const { t } = useTranslation(NAMESPACES.AUTH);
  const { login, loading: isLoading } = useLogin();

  // Get email screen animation class
  const getEmailScreenClass = () => {
    if (direction === 'none') {
      return 'animate-fade-in';
    }
    
    if (step === 'email') {
      return 'animate-fade-in';
    } else {
      return 'animate-fade-out';
    }
  };
  
  // Get password screen animation class
  const getPasswordScreenClass = () => {
    if (step === 'password') {
      return 'animate-fade-in';
    } else {
      return 'animate-fade-out';
    }
  };
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  
  const handleNextStep = () => {
    if (!email.trim()) return;
    
    setDirection('forward');
    // Add a small delay to allow animation to complete
    setTimeout(() => {
      setStep('password');
    }, 200);
  };
  
  const handleBack = () => {
    if (step === 'password') {
      setDirection('backward');
      // Add a small delay to allow animation to complete
      setTimeout(() => {
        setStep('email');
      }, 200);
    } else {
      navigate('/');
    }
  };
  
  const handleLogin = async () => {
    if (!password.trim() || isLoading) return;
    
    const credentials: LoginCredentials = {
      email,
      password
    };
    
    await login(credentials);
    // No need to reset anything on failure - errors are handled by toast
  };
  
  // Handle key press events
  const handleEmailKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && email.trim()) {
      handleNextStep();
    }
  };
  
  const handlePasswordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && password.trim() && !isLoading) {
      handleLogin();
    }
  };
  
  return (
    <div className="h-dvh flex flex-col bg-black text-white">
      <div className="flex-1 flex flex-col px-6">
        {/* Email Step */}
        {step === 'email' && (
          <div className={`flex-1 flex flex-col ${getEmailScreenClass()}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('login.welcomeBack')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('login.emailPrompt')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="email"
                  name="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={handleEmailChange}
                  onKeyPress={handleEmailKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('login.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleNextStep}
                disabled={!email.trim() || isLoading}
              >
                {t('login.next')}
              </button>
            </div>
          </div>
        )}
        
        {/* Password Step */}
        {step === 'password' && (
          <div className={`flex-1 flex flex-col ${getPasswordScreenClass()}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('login.enterPassword')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('login.accessAccount')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="password"
                  name="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyPress={handlePasswordKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
                
                {/* Forgot Password Link */}
                <div className="mt-4 flex justify-center">
                  <a 
                    onClick={() => !isLoading && navigate('/forgot-password')}
                    className={`text-center ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    style={{
                      color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '20px',
                      letterSpacing: '-0.16px',
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'solid',
                      textDecorationSkipInk: 'auto',
                      textDecorationThickness: '8.5%',
                      textUnderlineOffset: '10.5%',
                      textUnderlinePosition: 'from-font'
                    }}
                  >
                    {t('login.forgotPassword')}
                  </a>
                </div>
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('login.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleLogin}
                disabled={!password.trim() || isLoading}
              >
                {isLoading ? t('login.loggingIn') : t('login.submit')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login; 